import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PrescriptionDetailsModal } from '../components/prescription/PrescriptionDetailsModal';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { ChevronRightIcon, CheckCircleIcon, ClockIcon, PackageIcon, XCircleIcon, FilterIcon, SearchIcon } from 'lucide-react';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { prescriptionRecords } from '../data/prescriptionRecords';
export function AllPrescriptionsPage() {
  const [selectedPrescription, setSelectedPrescription] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const allPrescriptions = prescriptionRecords;
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-5868" data-mp="1e264ggnm386" />;
      case 'ready':
        return <PackageIcon className="w-5 h-5 text-blue-600" data-id="element-5869" data-mp="3sokgaltae2p3" />;
      case 'out-for-delivery':
        return <PackageIcon className="w-5 h-5 text-sky-600" data-id="element-5870" data-mp="3nqr9bqk5t4nr" />;
      case 'in-progress':
      case 'awaiting-approval':
      case 'expired':
        return <ClockIcon className="w-5 h-5 text-amber-600" data-id="element-5871" data-mp="p34ue75smubn" />;
      case 'insurance-rejected':
      case 'needs-contact':
        return <XCircleIcon className="w-5 h-5 text-rose-600" data-id="element-5872" data-mp="2eio1yi315kn1" />;
      default:
        return <XCircleIcon className="w-5 h-5 text-gray-500" data-id="element-5873" data-mp="2ekiizlb6kslu" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready for fulfillment';
      case 'out-for-delivery':
        return 'Out for delivery';
      case 'in-progress':
        return 'Pharmacist review';
      case 'awaiting-approval':
        return 'Awaiting insurance approval';
      case 'expired':
        return 'Prescription expired';
      case 'insurance-rejected':
        return 'Insurance claim rejected';
      case 'needs-contact':
        return 'Needs your attention';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Under review';
    }
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'ready':
        return 'bg-blue-100 text-blue-700';
      case 'out-for-delivery':
        return 'bg-sky-100 text-sky-700';
      case 'in-progress':
      case 'awaiting-approval':
      case 'expired':
        return 'bg-amber-100 text-amber-800';
      case 'insurance-rejected':
      case 'needs-contact':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getStatusAccent = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-500';
      case 'ready':
        return 'bg-blue-500';
      case 'out-for-delivery':
        return 'bg-sky-500';
      case 'in-progress':
      case 'awaiting-approval':
      case 'expired':
        return 'bg-amber-500';
      case 'insurance-rejected':
      case 'needs-contact':
        return 'bg-rose-500';
      default:
        return 'bg-gray-400';
    }
  };
  // Status pill counts for mobile
  const statusCounts = {
    all: allPrescriptions.length,
    'in-progress': allPrescriptions.filter(p => p.status === 'in-progress' || p.status === 'awaiting-approval').length,
    ready: allPrescriptions.filter(p => p.status === 'ready' || p.status === 'out-for-delivery').length,
    delivered: allPrescriptions.filter(p => p.status === 'delivered').length
  };
  const statusPills: {
    key: string;
    label: string;
  }[] = [{
    key: 'all',
    label: 'All'
  }, {
    key: 'in-progress',
    label: 'In Progress'
  }, {
    key: 'ready',
    label: 'Ready'
  }, {
    key: 'delivered',
    label: 'Delivered'
  }];
  const filteredPrescriptions = allPrescriptions.filter(prescription => {
    const matchesSearch = prescription.id.toLowerCase().includes(searchQuery.toLowerCase()) || prescription.items.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || statusFilter === 'in-progress' && (prescription.status === 'in-progress' || prescription.status === 'awaiting-approval') || statusFilter === 'ready' && (prescription.status === 'ready' || prescription.status === 'out-for-delivery') || prescription.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const handlePrescriptionClick = (prescription: any) => {
    setSelectedPrescription(prescription);
    setIsModalOpen(true);
  };
  return <HelmetProvider data-id="element-5874" data-mp="1c2dfe098tizg">
      <SEO title="Prescription History & Orders" description="View and track all your prescription orders. Check delivery status, reorder medications, and manage your prescription history in one convenient place." canonical="/en/prescription/history" hreflangAr="/prescription/history" hreflangEn="/en/prescription/history" hreflangDefault="/prescription/history" keywords="prescription history, order tracking, medication orders, prescription management, reorder medications, delivery status, prescription tracking Saudi Arabia" data-id="element-5875" data-mp="1c5rijapqdw7y" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-5876" data-mp="jhc3io6lndwm">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-5877" data-mp="ei9bc4bpx262">
          <AnnouncementBar data-id="element-5878" data-mp="4s0zkvkr71c8" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-5879" data-mp="4rrb5cfcx28n" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-5880" data-mp="3r00kjz8vkvcw" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-5881" data-mp="njk1t06yebd7">
          <h1 className="sr-only" data-id="element-5882" data-mp="shd61nop4vau">Prescription History & Order Tracking</h1>
          {/* Mobile Header Overlay */}
          <MobileInternalHeader title="Prescription History" data-id="element-5883" data-mp="drec5x46c616" />
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Prescription History'
        }]} title="Prescription History" description="View and track all your prescription orders, including current and past prescriptions." data-id="element-5884" data-mp="ir0om4c6hi8x" />
          <div className="container mx-auto px-3 md:px-8 lg:px-12 py-3 md:py-8" data-id="element-5885" data-mp="2qzfa80z4l201">
            {/* Search — sticky on mobile under header */}
            <div className="md:hidden sticky top-[56px] z-30 -mx-3 px-3 py-2 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200" data-id="element-5886" data-mp="30prr9j7qed1f">
              <div className="relative" data-id="element-5887" data-mp="2d18jd4slg29v">
                <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-5888" data-mp="2d29yplgn7axe" />
                <input type="text" placeholder="Search prescriptions or meds…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-5889" data-mp="283ff4haupic8" />
              </div>
              {/* Status pills */}
              <div className="flex gap-2 overflow-x-auto scrollbar-hide mt-2 -mx-1 px-1" data-id="element-5890" data-mp="1qtlbtdxm93al">
                {statusPills.map(pill => {
                const isActive = statusFilter === pill.key;
                const count = (statusCounts as any)[pill.key];
                return <button key={pill.key} onClick={() => setStatusFilter(pill.key)} className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${isActive ? 'bg-[#1E2B55] text-white' : 'bg-white text-gray-700 border border-gray-200'}`} data-id="element-5891" data-mp="25i7mv3g7n8w0">
                      {pill.label}
                      <span className={`ml-1.5 ${isActive ? 'opacity-80' : 'text-gray-400'}`} data-id="element-5892" data-mp="2oeqzw3u4xgxu">
                        {count}
                      </span>
                    </button>;
              })}
              </div>
            </div>
            {/* Desktop Filters */}
            <div className="hidden md:block bg-white rounded-xl shadow-md p-6 mb-6" data-id="element-5893" data-mp="32larzo23y6nz">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5894" data-mp="2yjgxxymf40ws">
                <div className="relative" data-id="element-5895" data-mp="2yjpa1oapv26x">
                  <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-5896" data-mp="2tkw72pt22tl0" />
                  <input type="text" placeholder="Search by prescription ID or medication..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-5897" data-mp="3q9lpqicibq81" />
                </div>
                <div className="relative" data-id="element-5898" data-mp="25sfehlbozj7g">
                  <FilterIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-5899" data-mp="20vfffud9fgpu" />
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent appearance-none" data-id="element-5900" data-mp="2mv2f18m15ekn">
                    <option value="all" data-id="element-5901" data-mp="35ks6pmuhxk8c">All Status</option>
                    <option value="delivered" data-id="element-5902" data-mp="35j7brw49rz9y">Delivered</option>
                    <option value="ready" data-id="element-5903" data-mp="30m76hnf8v0jw">Ready</option>
                    <option value="in-progress" data-id="element-5904" data-mp="30ns1tf9dunit">In Progress</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Prescriptions List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 mt-3 md:mt-0" data-id="element-5905" data-mp="3akmwfn2jc3rt">
              {filteredPrescriptions.map(prescription => {
              const itemCount = prescription.items.reduce((s, i) => s + i.quantity, 0);
              return <button key={prescription.id} onClick={() => handlePrescriptionClick(prescription)} className="relative bg-white rounded-2xl shadow-sm md:shadow-md overflow-hidden active:scale-[0.99] hover:shadow-lg hover:border-[#1E2B55] border border-gray-100 transition-all text-left" data-id="element-5906" data-mp="mwjbmbosykt">
                    {/* Mobile-optimized scannable layout */}
                    <div className="md:hidden flex" data-id="element-5907" data-mp="3d4jkizztj3ge">
                      {/* Status accent bar */}
                      <div className={`w-1 ${getStatusAccent(prescription.status)}`} data-id="element-5908" data-mp="2z5blq87khxcq" />
                      <div className="flex-1 p-3.5" data-id="element-5909" data-mp="343v7o8m5v953">
                        {/* Top row: status + date */}
                        <div className="flex items-center justify-between mb-2" data-id="element-5910" data-mp="xb2ihtpuayrp">
                          <div className="flex items-center gap-1.5" data-id="element-5911" data-mp="12bq8qd68vbkv">
                            {getStatusIcon(prescription.status)}
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${getStatusBadgeClass(prescription.status)}`} data-id="element-5912" data-mp="vvzsowjeyrcp">
                              {getStatusText(prescription.status)}
                            </span>
                          </div>
                          <span className="text-[11px] text-gray-500" data-id="element-5913" data-mp="1jlu5je3qgfwy">
                            {new Date(prescription.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                          </span>
                        </div>
                        {/* RX ID */}
                        <p className="text-[15px] font-bold text-gray-900 leading-tight" data-id="element-5914" data-mp="1xllntr1a8igr">
                          {prescription.id}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-5915" data-mp="25vsqiatm3vqe">
                          {prescription.doctor}
                        </p>
                        {/* Item thumbnails + count */}
                        <div className="flex items-center gap-2 mt-2.5" data-id="element-5916" data-mp="2etcx7za8uk8e">
                          <div className="flex -space-x-2" data-id="element-5917" data-mp="2juk13n99mz9s">
                            {prescription.items.slice(0, 3).map((item, idx) => <img key={idx} src={item.image} alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover bg-gray-100" data-id="element-5918" data-mp="2or0yz969os02" data-mpi={idx} />)}
                          </div>
                          <span className="text-xs text-gray-600 truncate" data-id="element-5919" data-mp="37l6b3k2tytmf">
                            {prescription.items.length} med
                            {prescription.items.length > 1 ? 's' : ''} ·{' '}
                            {itemCount} units
                          </span>
                        </div>
                        {/* Bottom: total + chevron */}
                        <div className="flex items-end justify-between mt-2.5 pt-2.5 border-t border-gray-100" data-id="element-5920" data-mp="mt74zvcq7hla">
                          <div data-id="element-5921" data-mp="hvnqcuc492st">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-5922" data-mp="hxi15ftobej6">
                              Total
                            </p>
                            <p className="text-base font-bold text-[#1E2B55]" data-id="element-5923" data-mp="3ldfbppvqyoi1">
                              SAR {prescription.total.toFixed(2)}
                            </p>
                          </div>
                          <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-5924" data-mp="3v8qrmbiw61rd" />
                        </div>
                      </div>
                    </div>
                    {/* Desktop layout — unchanged */}
                    <div className="hidden md:block p-6 h-full" data-id="element-5925" data-mp="1alvxzs9104t5">
                      <div className="flex flex-col h-full" data-id="element-5926" data-mp="1i2j2xupxu447">
                        <div className="flex items-start justify-between mb-4" data-id="element-5927" data-mp="1n25hc7hi5qel">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-5928" data-mp="1n02hywugmkx7">
                            {prescription.id}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadgeClass(prescription.status)}`} data-id="element-5929" data-mp="1d50oxpx2cypc">
                            {getStatusText(prescription.status)}
                          </span>
                        </div>
                        <div className="space-y-3 flex-grow" data-id="element-5930" data-mp="1r1xqalat5ykh">
                          <div data-id="element-5931" data-mp="1w1shyr2eqs6l">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-5932" data-mp="1w10iv69lqcbk">Date</p>
                            <p className="text-sm font-medium text-gray-900" data-id="element-5933" data-mp="2jr4plmi36s6s">
                              {new Date(prescription.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                            </p>
                          </div>
                          <div data-id="element-5934" data-mp="2kfbyniv1terk">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-5935" data-mp="2fg8wq7ad7ont">Doctor</p>
                            <p className="text-sm font-medium text-gray-900" data-id="element-5936" data-mp="2fidb5j5w462c">
                              {prescription.doctor}
                            </p>
                          </div>
                          <div data-id="element-5937" data-mp="25oml49u47nkj">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-5938" data-mp="21pnm15v2el10">Items</p>
                            <p className="text-sm text-gray-700 line-clamp-2" data-id="element-5939" data-mp="1wq18swf8l4s7">
                              {prescription.items.map(item => item.name).join(', ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200" data-id="element-5940" data-mp="c7gwx8hbath4">
                          <div data-id="element-5941" data-mp="h84opwdd7ece">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-5942" data-mp="h7uvlzx0wr2h">Total</p>
                            <p className="text-xl font-bold text-[#1E2B55]" data-id="element-5943" data-mp="l7dj93g8bg90">
                              SAR {prescription.total.toFixed(2)}
                            </p>
                          </div>
                          <ChevronRightIcon className="w-6 h-6 text-gray-400" data-id="element-5944" data-mp="3pnem4r6b7vdu" />
                        </div>
                      </div>
                    </div>
                  </button>;
            })}
            </div>
            {filteredPrescriptions.length === 0 && <div className="bg-white rounded-xl shadow-sm md:shadow-md p-8 md:p-12 text-center mt-3" data-id="element-5945" data-mp="31wixu412bibc">
                <PackageIcon className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-3 md:mb-4" data-id="element-5946" data-mp="35xkxun2szyed" />
                <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1.5 md:mb-2" data-id="element-5947" data-mp="1zc3gc2leyvtt">
                  No prescriptions found
                </h3>
                <p className="text-sm text-gray-600" data-id="element-5948" data-mp="29aak1jqozhdv">
                  Try adjusting your search or filter criteria
                </p>
              </div>}
          </div>
        </main>
        <Footer data-id="element-5949" data-mp="2ia5xxwbmtqhl" />
        <MobileFooterNav data-id="element-5950" data-mp="2i9nykqtoaw5g" />
        <PharmacistChat data-id="element-5951" data-mp="2i8uy27lb52vr" />
        <div className="h-16 md:hidden" data-id="element-5952" data-mp="2i8304q48mt2b"></div>
        {selectedPrescription && <PrescriptionDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} prescription={selectedPrescription} data-id="element-5953" data-mp="2db2w0kqy80du" />}
      </div>
    </HelmetProvider>;
}